import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Divider,
  Avatar,
  Button,
  LinearProgress,
  Select,
  MenuItem,
  TextField 
} from "@mui/material";
import { useParams } from "react-router-dom";
import api from "../../api/axios";

const user = JSON.parse(localStorage.getItem("user") || "null");
const isVendor = user?.role === "vendor";

const VendorReviews = () => {
  const { vendorId } = useParams();

  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sort, setSort] = useState("recent");
const [selectedMedia, setSelectedMedia] = useState(null);
const [currentIndex, setCurrentIndex] = useState(0);
const [replyText, setReplyText] = useState({});

  useEffect(() => {
    loadReviews();
    loadSummary();
  }, [vendorId, sort]);

  const loadReviews = async () => {
    try {
      const res = await api.get(`/reviews/vendor/${vendorId}?sort=${sort}`);
      setReviews(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadSummary = async () => {
    try {
      const res = await api.get(`/reviews/vendor-summary/${vendorId}`);
      setSummary(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>

      {/* HEADER */}
      <Typography variant="h5" fontWeight={700} mb={2}>
        Customer Reviews
      </Typography>

      {/* SUMMARY */}
      {summary && (
        <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography fontSize={36} fontWeight={700}>
              {(summary.average || 0).toFixed(1)}
            </Typography>
            <Typography fontSize={20}>⭐</Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            {(summary.total || 0).toLocaleString()} global ratings
          </Typography>

          <Box >
            {[5,4,3,2,1].map((star) => (
              <Stack key={star} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography sx={{ width: 40 }}>{star}★</Typography>
                <LinearProgress
                  variant="determinate"
                  value={summary.distribution?.[star] || 0}
                  sx={{ flex: 1, height: 8, borderRadius: 5 }}
                />
                <Typography sx={{ width: 40 }}>
                  {summary.distribution?.[star] || 0}%
                </Typography>
              </Stack>
            ))}
          </Box>
        </Paper>
      )}

      {/* SORT */}
      <Box sx={{ mb: 2 }}>
        <Select value={sort} onChange={(e) => setSort(e.target.value)} size="small">
          <MenuItem value="recent">Most Recent</MenuItem>
          <MenuItem value="helpful">Most Helpful</MenuItem>
          <MenuItem value="rating">Top Rated</MenuItem>
        </Select>
      </Box>

      {/* REVIEWS LIST */}
      {reviews.map((r) => {
  const name = r.userName || "Unknown User";
  const initial = name.charAt(0).toUpperCase();

  return (
    <Paper key={r._id} sx={{ p: 3, mb: 2, borderRadius: 2 }}>

      {/* USER */}
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar>{initial}</Avatar>
        <Typography fontWeight={600}>
          {name}
        </Typography>
      </Stack>

      {/* RATING */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
        <Typography>
          {calculateAvg(r.ratings).toFixed(1)} ⭐
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {new Date(r.createdAt).toLocaleDateString()}
        </Typography>
      </Stack>

      {/* TITLE */}
      <Typography fontWeight={700} sx={{ mt: 1 }}>
        {r.title}
      </Typography>

      {/* COMMENT */}
      <Typography sx={{ mt: 1 }}>
        {r.comment}
      </Typography>
{/* VENDOR REPLY */}
{r.vendorReply?.comment && (
  <Box
sx={{
  mt: 2,
  p: 2,
  bgcolor: "#f9fafb",
  borderRadius: 2,
  border: "1px solid #e0e0e0"
}}
  >
<Stack direction="row" spacing={1} alignItems="center">
  <Typography fontWeight={600} fontSize={14}>
    Vendor Response
  </Typography>
  <Typography
    sx={{
      fontSize: 11,
      bgcolor: "#1976d2",
      color: "#fff",
      px: 1,
      borderRadius: 1
    }}
  >
    Official
  </Typography>
</Stack>

    <Typography fontSize={14} sx={{ mt: 0.5 }}>
      {r.vendorReply.comment}
    </Typography>

    <Typography variant="caption" color="text.secondary">
      {new Date(r.vendorReply.updatedAt).toLocaleDateString()}
    </Typography>
  </Box>
)}

{isVendor && (
  <Box sx={{ mt: 2 }}>
<TextField
  fullWidth
  multiline
  minRows={2}
  value={replyText[r._id] || r.vendorReply?.comment || ""}
  onChange={(e) =>
    setReplyText({ ...replyText, [r._id]: e.target.value })
  }
  placeholder="Write a professional response..."
/>

    <Button
      size="small"
      sx={{ mt: 1 }}
      onClick={async () => {
        await api.post(`/reviews/${r._id}/reply`, {
          comment: replyText[r._id]
        });
        loadReviews(); // refresh
      }}
    >
      {r.vendorReply ? "Update Reply" : "Post Reply"}
    </Button>
  </Box>
)}

{/* MEDIA */}
{(r.mediaUrls?.images?.length > 0 || r.mediaUrls?.videos?.length > 0) && (
  <Box >
    <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
  Customer Photos & Videos
</Typography>
    
{/* IMAGES CAROUSEL */}
{r.mediaUrls?.images?.length > 0 && (
  <Stack direction="row" spacing={1} >
    {r.mediaUrls.images.slice(0, 5).map((img, idx) => (
      <Box
        key={idx}
        component="img"
        src={img.url}
        alt="review"
        onClick={() => {
          setSelectedMedia(r.mediaUrls.images);
          setCurrentIndex(idx);
        }}
        sx={{
          width: 70,
          height: 70,
          objectFit: "cover",
          borderRadius: 1,
          cursor: "pointer",
          border: "1px solid #ddd",
          "&:hover": { borderColor: "#000" }
        }}
      />
    ))}
  </Stack>
)}
    {/* VIDEOS */}
    {r.mediaUrls?.videos?.length > 0 && (
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
        {r.mediaUrls.videos.map((vid, idx) => (
          <Box key={idx}
          
          >
            <video
              width={160}
              height={100}
              controls
              poster={vid.thumbnailUrl || undefined}
              style={{ borderRadius: 8, cursor: "pointer" }}
            >
              <source src={vid.url} />
            </video>
          </Box>
        ))}
      </Stack>
    )}
  </Box>
)}
      {/* VERIFIED */}
      {r.isVerifiedPurchase && (
<Typography
  variant="caption"
  sx={{
    mt: 1,
    display: "inline-block",
    bgcolor: "#e6f4ea",
    color: "#1e7e34",
    px: 1,
    borderRadius: 1
  }}
>
  ✔ Verified Purchase
</Typography>
      )}

      {/* HELPFUL */}
<Button
  size="small"
  variant="outlined"
  sx={{ mt: 1, textTransform: "none" }}
>
  👍 Helpful ({r.helpfulCount || 0})
</Button>
    </Paper>
  );
})}
{selectedMedia && (
  <Box
    onClick={() => setSelectedMedia(null)}
    sx={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      bgcolor: "rgba(0,0,0,0.8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999
    }}
  >
    {/* LEFT ARROW */}
    <Button
      onClick={(e) => {
        e.stopPropagation();
        setCurrentIndex((prev) =>
          prev === 0 ? selectedMedia.length - 1 : prev - 1
        );
      }}
      sx={{ position: "absolute", left: 20, color: "#fff", fontSize: 30 }}
    >
      ◀
    </Button>

    {/* IMAGE */}
    <Box
      component="img"
      src={selectedMedia[currentIndex]?.url}
      alt="preview"
      onClick={(e) => e.stopPropagation()}
      sx={{
        maxWidth: "90%",
        maxHeight: "80%",
        borderRadius: 2
      }}
    />

    {/* RIGHT ARROW */}
    <Button
      onClick={(e) => {
        e.stopPropagation();
        setCurrentIndex((prev) =>
          prev === selectedMedia.length - 1 ? 0 : prev + 1
        );
      }}
      sx={{ position: "absolute", right: 20, color: "#fff", fontSize: 30 }}
    >
      ▶
    </Button>
  </Box>
)}

    </Box>
  );
};

/* ⭐ Calculate Avg Rating */
const calculateAvg = (ratings) => {
  if (!ratings) return 0;

  const values = [
    ratings.productQuality,
    ratings.valueForMoney,
    ratings.deliveryExperience,
    ratings.sellerService,
    ratings.agentExperience,
  ].filter(Boolean);

  return values.length
    ? values.reduce((a, b) => a + b, 0) / values.length
    : 0;
};

export default VendorReviews;